import { usePageTitle, BUSINESS } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";

const PAGES = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: July 2026",
    sections: [
      {
        h: "What we collect",
        b: "When you buy from Sitebound we collect your name (if provided), email address and order details. Payment card details are processed securely by Stripe — we never see or store your card number. If you contact us, we keep your message and email so we can respond.",
      },
      {
        h: "How we use it",
        b: "We use your information to deliver your digital products through your Job Seeker Hub, send order and account emails, keep your purchase history (if you create an account), respond to enquiries, and meet our legal and tax obligations in Australia.",
      },
      {
        h: "What we don't do",
        b: "We don't sell your personal information, we don't send marketing emails you haven't asked for, and we don't share your details with anyone except the service providers needed to run the store (Stripe for payments, our email delivery provider for transactional emails).",
      },
      {
        h: "Your rights",
        b: "Under the Privacy Act 1988 (Cth) you can request access to, or correction of, the personal information we hold about you. Contact us via the Contact page and we'll action it promptly.",
      },
      {
        h: "Cookies",
        b: "We use a small number of cookies: an authentication cookie if you sign in, and privacy-respecting analytics to understand how the store is used. No advertising trackers.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: July 2026",
    sections: [
      {
        h: "The store",
        b: "Sitebound sells digital products — downloadable job seeker packs for Australian construction, civil, mining and FIFO work. All prices are listed in Australian Dollars (AUD).",
      },
      {
        h: "Your purchase",
        b: "When you buy a pack you receive a non-exclusive, non-transferable licence for personal use. You may not on-sell, redistribute, or publish the files or your download link. Each purchase is for one person.",
      },
      {
        h: "Delivery",
        b: "Products are delivered digitally: instant access through your Job Seeker Hub after purchase, with a complete ZIP backup available to download from inside the Hub. You're responsible for providing a correct email address at checkout.",
      },
      {
        h: "No employment guarantee",
        b: "Sitebound packs are educational resources. They improve your job-readiness but do not guarantee employment, interviews, or any particular outcome. Ticket and licence information is general guidance — always confirm current requirements with the relevant state regulator or registered training organisation.",
      },
      {
        h: "Acceptable use",
        b: "You must be able to form a legally binding contract to purchase. Don't misuse the site, attempt to access systems you're not authorised to, or interfere with other customers.",
      },
      {
        h: "Changes",
        b: "We may update these terms from time to time. The version published on this page applies to purchases made while it is live.",
      },
    ],
  },
  refunds: {
    title: "Refund Policy",
    updated: "Last updated: July 2026",
    sections: [
      {
        h: "Digital products",
        b: "Sitebound packs are digital goods delivered instantly. Because access can't be 'returned', we generally don't offer change-of-mind refunds once your pack has been downloaded.",
      },
      {
        h: "Before you download",
        b: "If you've paid but haven't downloaded your pack yet and you've changed your mind, contact us within 14 days and we'll refund you in full to your original payment method.",
      },
      {
        h: "Australian Consumer Law",
        b: "Nothing in this policy limits your rights under Australian Consumer Law. If the product is faulty, not as described, or doesn't do what we said it would, you're entitled to a remedy — contact us and we'll make it right with a fix, replacement or refund as appropriate.",
      },
      {
        h: "Technical issues",
        b: "If your download link doesn't work or the files won't open, contact us first — most issues are fixed in minutes. If we can't get you a working copy, we'll refund you.",
      },
      {
        h: "How to request",
        b: "Use the Contact page with your order number (it starts with SB-) and the email you purchased with. Refunds are processed back to the original payment method via Stripe and typically land within 5–10 business days.",
      },
    ],
  },
};

export default function Legal({ kind }) {
  const page = PAGES[kind];
  usePageTitle(`${page.title} — Sitebound`, `Sitebound ${page.title} — Australian digital products store.`);
  return (
    <div data-testid={`legal-${kind}-page`} className="mx-auto max-w-3xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <Reveal>
        <div className="mono-label">Legal</div>
        <h1 className="font-display mt-4 text-5xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-6xl">
          {page.title}
        </h1>
        <p className="font-mono-ind mt-4 text-[11px] uppercase tracking-[0.2em] text-slate-500">
          {page.updated}
        </p>
      </Reveal>
      <div className="mt-12 space-y-10">
        {page.sections.map((s, i) => (
          <Reveal key={s.h} delay={i * 0.05}>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-100">
              <span className="font-mono-ind mr-3 text-sm text-[#FF5500]">
                {String(i + 1).padStart(2, "0")}
              </span>
              {s.h}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.b}</p>
          </Reveal>
        ))}
        <Reveal delay={0.1}>
          <div data-testid="legal-business-details" className="border-t border-white/10 pt-8">
            <div className="mono-label">Business details</div>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              {BUSINESS.name} — {BUSINESS.tagline} · ABN {BUSINESS.abn}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
              <a
                data-testid="legal-email-link"
                href={`mailto:${BUSINESS.email}`}
                className="text-[#FF5500] underline underline-offset-2"
              >
                {BUSINESS.email}
              </a>
              {" · "}
              <a
                data-testid="legal-website-link"
                href={BUSINESS.url}
                className="text-[#FF5500] underline underline-offset-2"
              >
                sitebound.com.au
              </a>
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
